import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import facade from "../services/qwof/apiFacade";

const DEBOUNCE_DELAY = 300;

/**
 * Custom hook for searching items with debouncing and React Query caching
 * 
 * @param {Object} options
 * @param {string} options.search - The search term
 * @param {string} options.type - The item type filter
 * @param {number} options.page - Current page number (0-indexed)
 * @param {number} options.limit - Items per page
 * @returns {Object} Query state with items, total count, isLoading, isFetching, isError, error, hasMore
 */
export function useItemSearch({
  search = "",
  type = "",
  page = 0,
  limit = 20,
}) {
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [debouncedType, setDebouncedType] = useState(type);

  // Debounce search and type filters
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedType(type);
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [type]);

  // Determine if we should fetch:
  // - Fetch if search is empty (show default list)
  // - Fetch if search length >= 2
  const shouldFetch = search === "" || search.length >= 2;

  const { data, isLoading, isFetching, isError, error } = useQuery({
    queryKey: ["items-search", debouncedSearch, debouncedType, page, limit],
    queryFn: async () => {
      if (!shouldFetch) {
        return { items: [], count: 0 };
      }

      try {
        const result = await facade.searchItems(
          debouncedSearch,
          debouncedType,
          page,
          limit
        );

        // Handle both direct array response and ItemResponseDTO structure
        if (Array.isArray(result)) {
          return { items: result, count: result.length };
        }

        return {
          items: Array.isArray(result.items) ? result.items : [],
          count: result.count ?? 0,
        };
      } catch (err) {
        console.error("Error searching items:", err);
        throw err;
      }
    },
    enabled: shouldFetch,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    keepPreviousData: true, // Keep showing old data while fetching new results
    refetchOnWindowFocus: false,
  });

  const items = data?.items ?? [];
  const totalCount = data?.count ?? 0;
  const hasMore = (page + 1) * limit < totalCount;

  return {
    items,
    totalCount,
    isLoading: isLoading && !data,
    isFetching,
    isError,
    error,
    hasMore,
  };
}
